import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Bookmark, Loader2, X } from 'lucide-react';
import { bookmarkStore } from '../lib/storage';
import { MathText, springs } from '../lib/utils-app';
import api from '../lib/api';

function EmptyState() {
  return (
    <div className="rounded-2xl glass hairline p-12 text-center">
      <div className="w-12 h-12 rounded-2xl bg-amber-400/10 ring-1 ring-amber-400/20 text-amber-300 flex items-center justify-center mx-auto">
        <Bookmark className="w-5 h-5" />
      </div>
      <div className="font-serif-q text-[20px] text-slate-100 mt-4">Nothing saved yet</div>
      <div className="text-[13px] text-slate-500 mt-1.5">Bookmark a question while practicing to find it here.</div>
      <Link to="/" className="mt-5 inline-flex px-4 h-10 items-center rounded-lg bg-indigo-500 hover:bg-indigo-400 text-slate-950 text-[13px] font-medium">Browse the library</Link>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="rounded-2xl glass hairline p-12 flex items-center justify-center text-slate-400 gap-2">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading saved questions…
    </div>
  );
}

function BookmarkCard({ q, delay, onRemove }) {
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ ...springs.soft, delay }}>
      <div className="flex items-stretch gap-2">
        <Link to={`/subject/${q.subject || 'math'}/section/${q.sectionSlug}/topic/${q.topicSlug}`}
          className="flex-1 block rounded-xl glass hairline px-5 py-4 hover:bg-white/[0.04] transition">
          <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.16em] text-slate-500 mb-1.5">
            <span>{q.section}</span><span>·</span><span>{q.topic}</span>
            {q.year && <><span>·</span><span className="font-mono">{q.year}</span></>}
          </div>
          <div className="font-serif-q text-[16px] text-slate-100 leading-relaxed line-clamp-2">
            <MathText text={q.question} />
          </div>
        </Link>
        <button onClick={onRemove} title="Remove"
          className="shrink-0 w-10 rounded-xl glass hairline text-slate-400 hover:text-rose-300 hover:bg-rose-400/[0.06] transition flex items-center justify-center">
          <X className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );
}

function BookmarksBody({ ids, items, onRemove }) {
  if (ids.length === 0) return <EmptyState />;
  if (!items) return <LoadingState />;
  return (
    <div className="grid grid-cols-1 gap-3">
      {items.map((q, i) => (
        <BookmarkCard key={q.id} q={q} delay={i * 0.03} onRemove={() => onRemove(q.id)} />
      ))}
    </div>
  );
}

export default function Bookmarks() {
  const [ids, setIds] = useState(() => bookmarkStore.list());
  const [items, setItems] = useState(null);

  useEffect(() => {
    let alive = true;
    if (ids.length === 0) { setItems([]); return undefined; }
    Promise.all(ids.map((id) => api.getQuestion(id).catch(() => null)))
      .then((arr) => { if (alive) setItems(arr.filter(Boolean)); });
    return () => { alive = false; };
  }, [ids]);

  const remove = (id) => {
    bookmarkStore.toggle(id);
    setIds(bookmarkStore.list());
  };

  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-2">Saved</div>
      <h1 className="font-serif-q text-[34px] tracking-tight text-slate-50 leading-tight mb-1">Bookmarks</h1>
      <p className="text-[13.5px] text-slate-400 mb-7">{ids.length} question{ids.length === 1 ? '' : 's'} saved for revisiting.</p>
      <BookmarksBody ids={ids} items={items} onRemove={remove} />
    </div>
  );
}
